// src/components/daily/DailyPlanPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getDailyTasksForUser,
  getTodayCompletion,
  saveTaskCompletion,
  checkAndUpdateDayCompletion,
  getIndiaDate,
  getIndiaDateString,
  checkAndUnlockAchievements,
  getFAQs,
  getYesterdayCompletionCount,
} from '../../services/firebase/firestoreService';
import { db } from '../../services/firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import EnhancedCamera from '../exercises/EnhancedCamera';
import PlankCamera from '../exercises/PlankCamera';
import RewardScreen from '../rewards/RewardScreen';
import './DailyPlanPage.css';

const DAY_FOCUS = {
  1: '800m/1600m चा सराव',
  2: '100m + गोळाफेक सराव',
  3: 'हलका सराव व सामूहिक खेळ',
  4: 'शक्तीचा सखोल सराव',
  5: 'रिकव्हरी सराव',
  6: '100m + गोळाफेक — कठीण सराव'
};

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="white" width="20" height="20">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
);

const TaskIcon = ({ type, completed }) => {
  const style = completed ? { filter: 'brightness(0) invert(1)' } : {};
  const src = type === 'strength' ? '/icon-strength.svg'
    : type === 'athletics' ? '/icon-athletics.svg'
    : '/icon-nutrition.svg';
  return <img src={src} alt={type} width="22" height="22" style={style} />;
};

const DonutProgress = ({ completed, total }) => {
  const size = 72;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = total > 0 ? (completed / total) * circumference : 0;
  const offset = circumference - progress;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="rgb(255, 255, 255)" strokeWidth={strokeWidth}/>
      <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#28a745" strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 0.4s ease' }}/>
    </svg>
  );
};

const VideoModal = ({ url, onClose }) => {
  const getYouTubeId = (u) => {
    const match = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([^&\n?#/]+)/);
    return match ? match[1] : '';
  };
  return (
    <div className="video-overlay" onClick={onClose}>
      <div className="video-modal" onClick={e => e.stopPropagation()}>
        <button className="video-close" onClick={onClose}>✕</button>
        <div className="video-wrapper">
          <iframe src={`https://www.youtube.com/embed/${getYouTubeId(url)}`}
            title="Exercise Video" frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen/>
        </div>
      </div>
    </div>
  );
};

const FALLBACK_FAQS = {
  en: [
    { q: 'Camera is not opening. What should I do?', a: 'Contact your admin on WhatsApp and they will help you fix it.' },
    { q: 'I completed my tasks but did not get a point. What happened?', a: 'Close the app and open it again. If the problem continues, contact your admin.' },
    { q: 'I missed a day of training. Will my streak break?', a: 'If you miss 2 days in a row, your streak will reset. Missing 1 day is okay — just train the next day.' }
  ],
  mr: [
    { q: 'कॅमेरा उघडत नाही. मी काय करू?', a: 'तुमच्या admin ला WhatsApp वर संपर्क करा, ते तुम्हाला मदत करतील.' },
    { q: 'मी कामे पूर्ण केली पण गुण मिळाला नाही. काय झाले?', a: 'अ‍ॅप बंद करा आणि पुन्हा उघडा. समस्या कायम राहिल्यास admin शी संपर्क साधा.' },
    { q: 'मी एक दिवस प्रशिक्षण चुकवले. माझी स्ट्रीक संपेल का?', a: 'सलग 2 दिवस चुकवल्यास स्ट्रीक रीसेट होते. 1 दिवस चुकवणे ठीक आहे — फक्त पुढच्या दिवशी प्रशिक्षण घ्या.' }
  ]
};

const FAQModal = ({ language, faqs, onClose }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="faq-overlay" onClick={onClose}>
      <div className="faq-modal" onClick={e => e.stopPropagation()}>
        <div className="faq-header">
          <span className="faq-title">{language === 'mr' ? 'सामान्य प्रश्न' : 'FAQs'}</span>
          <button className="faq-close" onClick={onClose}>✕</button>
        </div>
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <div key={i} className="faq-item">
              <button
                className={`faq-question ${openIndex === i ? 'open' : ''}`}
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span>{faq.q}</span>
                <span className="faq-arrow">{openIndex === i ? '▲' : '▼'}</span>
              </button>
              {openIndex === i && <div className="faq-answer">{faq.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DailyPlanPage = ({ day, onBack }) => {
  const { currentUser, userData, refreshUserData } = useAuth();
  const [language, setLanguage] = useState(() =>
    localStorage.getItem('spocademy_language') || 'mr'
  );
  const [tasks, setTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState(new Set());
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [showWhatsAppSupport, setShowWhatsAppSupport] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [faqList, setFaqList] = useState(FALLBACK_FAQS[language] || FALLBACK_FAQS.mr);
  const [videoUrl, setVideoUrl] = useState(null);
  const [cameraFallbackEnabled, setCameraFallbackEnabled] = useState(false);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [yesterdayCount, setYesterdayCount] = useState(0);
  const audioContextRef = useRef(null);
  const celebrationHandledRef = useRef(false);
  const rewardPreloadedRef = useRef(false);
  const preloadedRewardDataRef = useRef(null);

  const translations = {
    en: {
      day: 'Day', markComplete: 'Mark Complete', startCamera: 'Start Camera',
      completed: '✓ Completed', loadingTasks: 'Loading your training plan...',
      noTasksFound: 'No tasks planned for today', contactAdmin: 'Contact admin to add tasks for today',
      whatsappSupport: 'Contact Support', back: '← Back', video: 'video', faqs: 'FAQs'
    },
    mr: {
      day: 'दिवस', markComplete: 'पूर्ण झाले', startCamera: 'कॅमेरा सुरू करा',
      completed: '✓ पूर्ण झाले', loadingTasks: 'प्रशिक्षण योजना लोड होत आहे...',
      noTasksFound: 'आजसाठी कोणती कामे नाहीत', contactAdmin: 'कामे जोडण्यासाठी admin शी संपर्क साधा',
      whatsappSupport: 'सहाय्यता संपर्क', back: '← मागे', video: 'video', faqs: 'सामान्य प्रश्न'
    }
  };

  const t = translations[language];

  const handleLanguageToggle = () => {
    const newLang = language === 'en' ? 'mr' : 'en';
    setLanguage(newLang);
    localStorage.setItem('spocademy_language', newLang);
    setFaqList(FALLBACK_FAQS[newLang]);
  };

  const india = getIndiaDate();
  const days = language === 'en'
    ? ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday']
    : ['रविवार','सोमवार','मंगळवार','बुधवार','गुरुवार','शुक्रवार','शनिवार'];
  const months = language === 'en'
    ? ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
    : ['जाने','फेब्रु','मार्च','एप्रिल','मे','जून','जुलै','ऑग','सप्टें','ऑक्टो','नोव्हें','डिसें'];
  const dateString = `${days[india.getDay()]}, ${months[india.getMonth()]} ${india.getDate()}`;
  const firstName = userData?.firstName || '';
  const currentDay = userData?.fitnessCurrentDay || 1;

  const preloadRewardData = useCallback(async () => {
    if (rewardPreloadedRef.current || !currentUser?.uid || !userData) return;
    rewardPreloadedRef.current = true;
    try {
      const snap = await getDocs(collection(db, 'rewardContent'));
      const allDocs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = getIndiaDateString(yesterday);
      const yesterdaySnap = await getDoc(doc(db, 'fitnessDailyCompletions', `${currentUser.uid}_${yesterdayStr}`));
      const lastRewardType = yesterdaySnap.exists() ? yesterdaySnap.data()?.lastRewardType : null;

      let villageEligible = true;
      let villagePoints = 0;
      const village = userData.village || '';
      if (village) {
        const villageSlug = village.toLowerCase().replace(/\s+/g, '_');
        const villageSnap = await getDoc(doc(db, 'villages', villageSlug));
        if (villageSnap.exists()) {
          villagePoints = villageSnap.data().totalPoints || 0;
        }
      }
      preloadedRewardDataRef.current = { allDocs, lastRewardType, villageEligible, villagePoints };
    } catch (e) {
      preloadedRewardDataRef.current = null;
    }
  }, [currentUser?.uid, userData]);

  useEffect(() => {
    getYesterdayCompletionCount().then(setYesterdayCount);
  }, []);

  useEffect(() => {
    if (!userData?.fitnessLevel || !currentUser) return;
    const loadData = async () => {
      try {
        setLoadingTasks(true);
        setCameraFallbackEnabled(userData?.cameraFallbackEnabled || false);
        const [tasksResponse, todayCompletion] = await Promise.all([
          getDailyTasksForUser(userData.fitnessLevel, currentDay, userData),
          getTodayCompletion(currentUser.uid)
        ]);
        // Today's fitness day is already recorded — refuse to start again.
        if (todayCompletion?.allTasksCompleted === true) {
          setAlreadyDone(true);
          return;
        }
        if (tasksResponse.showWhatsAppSupport) {
          setShowWhatsAppSupport(true);
          setTasks([]);
        } else {
          const loadedTasks = tasksResponse.tasks || [];
          setTasks(loadedTasks);
          if (todayCompletion?.completedTasks) {
            setCompletedTasks(new Set(Object.keys(todayCompletion.completedTasks)));
          }
          if (loadedTasks.length <= 2) preloadRewardData();
        }
      } catch (e) {
        setShowWhatsAppSupport(true);
      } finally {
        setLoadingTasks(false);
      }
    };
    loadData();
  }, [userData, currentDay, currentUser, preloadRewardData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tasks.length === 0) return;
    const remaining = tasks.length - completedTasks.size;
    if (remaining <= 2 && remaining > 0) preloadRewardData();
  }, [completedTasks.size, tasks.length, preloadRewardData]);

  useEffect(() => {
    if (
      tasks.length > 0 &&
      completedTasks.size === tasks.length &&
      !celebrationHandledRef.current &&
      currentUser
    ) {
      celebrationHandledRef.current = true;
      const handleCompletion = async () => {
        try {
          const completed = await checkAndUpdateDayCompletion(
            currentUser.uid, currentDay, tasks.length
          );
          if (completed) {
            triggerHaptic('success');
            setTimeout(() => setShowReward(true), 600);
          }
        } catch (e) {}
      };
      handleCompletion();
    }
  }, [completedTasks.size, tasks.length, currentUser, currentDay]);

  const initAudio = () => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      } catch (e) {}
    }
  };

  const triggerHaptic = (type) => {
    if (!navigator.vibrate) return;
    if (type === 'light') navigator.vibrate(50);
    if (type === 'success') navigator.vibrate([100, 50, 100, 50, 200]);
  };

  const playSuccessSound = () => {
    initAudio();
    if (!audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(523, ctx.currentTime);
    osc.frequency.setValueAtTime(784, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  };

  const playAudio = (task) => {
    initAudio();
    if (!('speechSynthesis' in window)) return;
    const taskName = typeof task.name === 'object'
      ? (task.name[language] || task.name.en) : task.name;
    let text = taskName;
    if (task.sets && task.reps) {
      text += language === 'mr'
        ? `. ${task.sets} सेट, ${task.reps} रेप्स`
        : `. ${task.sets} sets of ${task.reps} reps`;
      if (task.restTime) {
        text += language === 'mr'
          ? `, ${task.restTime} सेकंद विश्रांती`
          : `, ${task.restTime} seconds rest`;
      }
    }
    speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = language === 'mr' ? 'hi-IN' : 'en-US';
    utterance.rate = 0.85;
    speechSynthesis.speak(utterance);
  };

  const handleTaskComplete = async (taskId) => {
    if (completedTasks.has(taskId) || !currentUser) return;
    initAudio();
    triggerHaptic('light');
    playSuccessSound();
    try {
      await saveTaskCompletion(currentUser.uid, taskId, currentDay);
      setCompletedTasks(prev => new Set([...prev, taskId]));
    } catch (e) {}
  };

  const handleFAQOpen = async () => {
    try {
      const all = await getFAQs();
      const active = all
        .filter(f => f.active === true)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      if (active.length > 0) {
        setFaqList(active.map(f => ({
          q: language === 'mr' ? f.questionMr : f.questionEn,
          a: language === 'mr' ? f.answerMr : f.answerEn
        })));
      }
    } catch (e) {}
    setShowFAQ(true);
  };

  const handleRewardComplete = async () => {
    setShowReward(false);
    try {
      const newlyUnlocked = await checkAndUnlockAchievements(currentUser.uid);
      if (newlyUnlocked.length > 0) {
        const key = `newAchievements_${currentUser.uid}`;
        const existing = JSON.parse(localStorage.getItem(key) || '[]');
        localStorage.setItem(key, JSON.stringify([...existing, ...newlyUnlocked.map(a => a.id)]));

        const celebrateOnes = newlyUnlocked.filter(a => a.celebrate === true);
        if (celebrateOnes.length > 0) {
          const celKey = `celebrateAchievements_${currentUser.uid}`;
          const existingCel = JSON.parse(localStorage.getItem(celKey) || '[]');
          const toStore = celebrateOnes.map(a => ({
            id: a.id, category: a.category, title: a.title, description: a.description
          }));
          localStorage.setItem(celKey, JSON.stringify([...existingCel, ...toStore]));
        }
      }
      if (refreshUserData) await refreshUserData();
    } catch (e) {}
    if (onBack) onBack(true);
  };

  const handleWhatsApp = () => {
    const message = language === 'mr'
      ? `नमस्कार, माझ्यासाठी आजची कामे अजून जोडलेली नाहीत. दिवस ${currentDay}.`
      : `Hi, no tasks assigned for me today. Day ${currentDay}.`;
    window.open(`https://wa.me/919359246193?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getTaskDescription = (task) => {
    const parts = [];
    if (task.sets && task.reps) {
      parts.push(language === 'mr'
        ? `${task.reps} रेप्स × ${task.sets} सेट`
        : `${task.reps} reps × ${task.sets} sets`);
    } else if (task.reps) {
      parts.push(language === 'mr' ? `${task.reps} रेप्स` : `${task.reps} reps`);
    } else if (task.sets) {
      parts.push(language === 'mr' ? `${task.sets} सेट` : `${task.sets} sets`);
    }
    if (task.restTime) {
      parts.push(language === 'mr'
        ? `${task.restTime} सेकंद विश्रांती`
        : `${task.restTime}s rest`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  };

  const renderTaskCard = (task) => {
    const isCompleted = completedTasks.has(task.id);
    const taskName = typeof task.name === 'object'
      ? (task.name[language] || task.name.en) : task.name;
    const description = getTaskDescription(task);
    const useCamera = task.hasMediaPipe && !cameraFallbackEnabled;

    return (
      <div key={task.id} className={`task-card ${isCompleted ? 'completed' : ''}`}>
        {task.youtubeURL && (
          <button className="video-btn" onClick={() => setVideoUrl(task.youtubeURL)}>
            <svg viewBox="0 0 24 24" width="14" height="14" fill="rgba(220,0,0,0.6)">
              <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
            </svg>
            <span>{t.video}</span>
          </button>
        )}
        <div className="task-header">
          <div className="task-icon-wrap">
            <TaskIcon type={task.type} completed={isCompleted} />
          </div>
          <div className="task-info">
            <div className="task-name">{taskName}</div>
            {description && <div className="task-description">{description}</div>}
            {task.comment && <div className="task-comment">{task.comment}</div>}
          </div>
        </div>
        <div className="task-controls">
          <button className="audio-btn" onClick={() => playAudio(task)}>🔊</button>
          {isCompleted ? (
            <div className="completed-indicator">{t.completed}</div>
          ) : (
            <button className="start-btn" onClick={() => {
              if (useCamera) setCurrentExercise(task);
              else handleTaskComplete(task.id);
            }}>
              {useCamera ? t.startCamera : t.markComplete}
            </button>
          )}
        </div>
      </div>
    );
  };

  if (showReward) {
    return (
      <RewardScreen
        userId={currentUser.uid}
        userData={userData}
        language={language}
        preloadedData={preloadedRewardDataRef.current}
        onComplete={handleRewardComplete}
      />
    );
  }

  if (alreadyDone) {
    const msg = language === 'mr'
      ? { title: 'आजचे प्रशिक्षण पूर्ण झाले आहे', sub: 'उद्या पुन्हा भेटू!', btn: 'घरी जा' }
      : { title: "Today's training is already complete", sub: 'See you tomorrow!', btn: 'Go Home' };
    return (
      <div className="daily-plan-container">
        <div className="task-list">
          <div className="dp-state-message">
            <div className="dp-state-icon">✅</div>
            <p className="dp-state-title">{msg.title}</p>
            <p className="dp-state-sub">{msg.sub}</p>
            <button
              onClick={() => onBack(true)}
              style={{ marginTop: '16px', padding: '14px 28px', background: '#007fff',
                color: '#fff', border: 'none', borderRadius: '10px',
                fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}>
              {msg.btn}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="daily-plan-container">
      <div className="dp-header">
        <div className="dp-header-top">
          <button className="back-btn" onClick={onBack}>{t.back}</button>
          <button className="lang-toggle-dp" onClick={handleLanguageToggle}>
            {language === 'en' ? 'मराठी' : 'EN'}
          </button>
        </div>
        <div className="dp-header-center">
          <DonutProgress completed={completedTasks.size} total={tasks.length} />
          <span className="dp-progress-text">{completedTasks.size}/{tasks.length}</span>
        </div>
        <div className="dp-header-bottom">
          <div className="dp-left">
            <div className="dp-user-name">{firstName}</div>
            <div className="dp-date">{dateString}</div>
          </div>
          <div className="training-day-badge">{t.day} {currentDay}</div>
        </div>
      </div>

      {DAY_FOCUS[india.getDay()] && (
        <div className="dp-day-focus-band">
          <div className="dp-day-focus-label">आजचे लक्ष्य</div>
          <div className="dp-day-focus-value">{DAY_FOCUS[india.getDay()]}</div>
        </div>
      )}

      <div className="task-list">
        {loadingTasks ? (
          <div className="dp-state-message">
            <div className="dp-spinner" />
            <p>{t.loadingTasks}</p>
          </div>
        ) : showWhatsAppSupport ? (
          <div className="dp-state-message">
            <div className="dp-state-icon">📋</div>
            <p className="dp-state-title">{t.noTasksFound}</p>
            <p className="dp-state-sub">{t.contactAdmin}</p>
            <button className="whatsapp-support-btn" onClick={handleWhatsApp}>
              <WhatsAppIcon />
              {t.whatsappSupport}
            </button>
          </div>
        ) : (
          <>
            {yesterdayCount >= 10 && (
              <div className="community-stat-card">
                                <span className="community-stat-text">
                  {language === 'mr'
                    ? `काल ${yesterdayCount} जणांनी सराव पूर्ण केला`
                    : `${yesterdayCount} users completed training yesterday`}
                </span>
                <span className="community-stat-icon">✅</span>
              </div>
            )}
            {tasks.map(task => renderTaskCard(task))}
            <button className="faq-trigger-btn" onClick={handleFAQOpen}>
              <div className="faq-trigger-icon">?</div>
            </button>
          </>
        )}
      </div>

      {videoUrl && <VideoModal url={videoUrl} onClose={() => setVideoUrl(null)} />}
      {showFAQ && <FAQModal language={language} faqs={faqList} onClose={() => setShowFAQ(false)} />}

      {currentExercise && currentExercise.mediaPipeType === 'plank' && (
        <PlankCamera
          exercise={currentExercise}
          userId={currentUser ? currentUser.uid : ''}
          onComplete={() => {
            handleTaskComplete(currentExercise.id);
            setCurrentExercise(null);
          }}
          onBack={() => setCurrentExercise(null)}
        />
      )}
      {currentExercise && currentExercise.mediaPipeType !== 'plank' && (
        <EnhancedCamera
          exercise={currentExercise}
          userId={currentUser ? currentUser.uid : ''}
          onComplete={() => {
            handleTaskComplete(currentExercise.id);
            setCurrentExercise(null);
          }}
          onBack={() => setCurrentExercise(null)}
        />
      )}
    </div>
  );
};

export default DailyPlanPage;